/**
 * Right-side task conversation panel for the single-order task screen.
 *
 * Layout: fixed header and composer footer; only the message list scrolls.
 *
 * @module tasksConversationPanel
 */
import { ChatMessage, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";

import type {
  OncoemrTaskThreadPdfLink,
  OncoemrThreadOutboxStatus,
} from "../../../../../data-model/oncoemrTaskMessagesModel";
import TaskConversationLinkIcon from "../../../../../svg/taskConversationLinkIcon";
import {
  getTaskMessageDeliveryAccessoryAriaLabel,
  TaskMessageDeliveryAccessory,
} from "../helpers/taskMessageDeliveryAccessory";
import { useTaskConversationMessageScroll } from "../hooks/useTaskConversationMessageScroll";
import TaskPdfViewerModal from "./taskPdfViewerModal";
import TasksConversationFooter from "./tasksConversationFooter";
import "./tasksConversationPanel.scss";

/**
 * Represents one chat item displayed in the task conversation panel.
 */
export interface TaskConversationMessage {
  id: string;
  senderName: string;
  timestamp: string;
  message: string;
  side: "left" | "right";
  avatarLabel?: string;
  /** Set for outbox rows still pending, sending, or failed in OncoEMR. */
  deliveryStatus?: OncoemrThreadOutboxStatus;
  /** Populated when `deliveryStatus` is `failed`. */
  lastError?: string;
  /** Outbox idempotency key; required to retry a failed outbound message. */
  idempotencyKey?: string;
}

/**
 * Represents the conversation details rendered for a selected task.
 */
export interface TaskConversationData {
  taskId: string;
  title: string;
  status: string;
  statusVariant?: "inProgress" | "open" | "completed";
  linkLabel: string;
  /** PDF attachments from `GET .../messages` (`pdf_links`; viewer uses `gcs_path`). */
  pdfLinks: OncoemrTaskThreadPdfLink[];
  messages: TaskConversationMessage[];
  /** True while `GET .../messages` is loading for the selected row. */
  messagesLoading?: boolean;
  /** Populated when the messages request fails for the selected row. */
  messagesError?: string | null;
}

/**
 * Props for {@link TasksConversationPanel}.
 */
interface TasksConversationPanelProps {
  taskConversation?: TaskConversationData;
  onSendMessage?: (body: string) => Promise<void>;
  isSendingMessage?: boolean;
  /** Re-queues a failed outbox reply by idempotency key. */
  onRetryFailedReply?: (idempotencyKey: string) => void | Promise<void>;
}

/**
 * Returns colors used by the task status tag.
 *
 * @param statusVariant - Status variant used to map colors.
 * @returns Background and text colors for the status tag.
 */
export const getStatusTagColors = (
  statusVariant: TaskConversationData["statusVariant"] = "open",
) => {
  if (statusVariant === "inProgress") {
    return {
      backgroundColor: "#E6F3F0",
      textColor: "#005D49",
    };
  }

  if (statusVariant === "completed") {
    return {
      backgroundColor: "#EAF3FF",
      textColor: "#0052CC",
    };
  }

  return {
    backgroundColor: "#FFF3E0",
    textColor: "#C24400",
  };
};

/**
 * Renders the right-side task details and two-way chat thread.
 *
 * @param props - Selected task conversation details.
 * @returns Conversation panel with header and chat timeline.
 */
const TasksConversationPanel = ({
  taskConversation,
  onSendMessage,
  isSendingMessage = false,
  onRetryFailedReply,
}: TasksConversationPanelProps) => {
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  useEffect(() => {
    setIsPdfViewerOpen(false);
  }, [taskConversation?.taskId]);

  const messagesContainerRef = useTaskConversationMessageScroll({
    taskId: taskConversation?.taskId ?? "",
    messagesLoading: taskConversation?.messagesLoading,
    messagesError: taskConversation?.messagesError,
    messageCount: taskConversation?.messages.length ?? 0,
  });

  if (!taskConversation) {
    return (
      <section className="flex min-h-0 flex-1 items-center justify-center px-6 py-8 text-sm text-primaryGray-7">
        No details found for this task.
      </section>
    );
  }

  const statusTagColors = getStatusTagColors(taskConversation.statusVariant);
  const hasPdfLinks = taskConversation.pdfLinks.length > 0;
  const showPdfLinkButton = hasPdfLinks && Boolean(taskConversation.linkLabel);

  return (
    <section className="tasks-conversation-panel flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center justify-between border-b border-primaryGray-14 px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-[#0F0F0F]">
            {taskConversation.title}
          </h3>
          <Tags
            label={taskConversation.status}
            backgroundColor={statusTagColors.backgroundColor}
            textColor={statusTagColors.textColor}
            className="tasks-conversation-panel__status-tag"
          />
        </div>

        {showPdfLinkButton ? (
          <button
            type="button"
            className="flex items-center gap-1 text-[0.75rem] font-bold text-[#06F]"
            onClick={() => setIsPdfViewerOpen(true)}
            aria-label={taskConversation.linkLabel}
          >
            <TaskConversationLinkIcon className="aspect-square h-4 w-4 shrink-0" />
            <span>{taskConversation.linkLabel}</span>
          </button>
        ) : null}
      </header>

      <div
        ref={messagesContainerRef}
        className="tasks-conversation-panel__messages flex-1 overflow-auto px-3 py-4"
      >
        {taskConversation.messagesError ? (
          <div className="font-lato shrink-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {taskConversation.messagesError}
          </div>
        ) : null}

        {taskConversation.messagesLoading ? (
          <div className="font-lato flex flex-1 items-center justify-center py-6 text-sm text-primaryGray-3">
            Loading messages…
          </div>
        ) : null}

        {!taskConversation.messagesLoading &&
        !taskConversation.messagesError ? (
          <div className="flex min-h-full flex-col justify-end gap-4">
            {taskConversation.messages.length === 0 ? (
              <p className="font-lato py-4 text-center text-sm text-primaryGray-3">
                No messages in this thread yet.
              </p>
            ) : (
              taskConversation.messages.map((message) => {
                const canRetry =
                  message.deliveryStatus === "failed" &&
                  Boolean(message.idempotencyKey) &&
                  Boolean(onRetryFailedReply);

                return (
                  <ChatMessage
                    key={message.id}
                    senderName={message.senderName}
                    timestamp={message.timestamp}
                    message={message.message}
                    side={message.side}
                    avatarLabel={message.avatarLabel}
                    accessory={
                      message.deliveryStatus ? (
                        <TaskMessageDeliveryAccessory
                          status={message.deliveryStatus}
                          lastError={message.lastError}
                          onRetry={
                            canRetry
                              ? () =>
                                  onRetryFailedReply!(message.idempotencyKey!)
                              : undefined
                          }
                        />
                      ) : undefined
                    }
                    accessoryAriaLabel={
                      message.deliveryStatus
                        ? getTaskMessageDeliveryAccessoryAriaLabel(
                            message.deliveryStatus,
                            message.lastError,
                          )
                        : undefined
                    }
                    bubbleClassName="tasks-conversation-panel__bubble"
                    maxWidth="75%"
                  />
                );
              })
            )}
          </div>
        ) : null}
      </div>

      <TasksConversationFooter
        onSendMessage={onSendMessage}
        isSendingMessage={isSendingMessage}
      />

      {isPdfViewerOpen && hasPdfLinks ? (
        <TaskPdfViewerModal
          pdfLinks={taskConversation.pdfLinks}
          taskTitle={taskConversation.title}
          onClose={() => setIsPdfViewerOpen(false)}
        />
      ) : null}
    </section>
  );
};

export default TasksConversationPanel;
